import React, { useState, useEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import CreatableSelect from "react-select/creatable";
import { useDrag, useDrop } from "react-dnd";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchBasicItinerary,
  fetchItineraryFoodMaster,
  fetchItineraryAttractionMaster,
  deleteItineraryAttractionMaster,
  updateItinerary,
  reorderItinerary,
  deleteItineraryDay,
} from "../packageService";
import Toster from "../../../common/Toster";
import Dialog from "../../../dialog/Dialog";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import FilePreview from "../../../FilePreview/FilePreview";
import TinyEditor from "../../../Editor/TinyEditor";
import { creatableStyleSingle, selectMenuPortalProps } from "../../../styles/creatableStyle";
import ImageInput from "../../../common/ImageInput";
import { pbAddBtnMt, pbDeleteBtn } from "../packageBuilderUi";
import { hasRichTextContent } from "../../../../utils/richTextUtils";

const SortableDayCard = ({ dayIndex, moveDay, children }) => {
  const ref = useRef(null);
  const dragHandleRef = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "itinerary-day",
    item: { index: dayIndex },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "itinerary-day",
    hover(draggedItem, monitor) {
      if (!ref.current) return;
      if (draggedItem.index === dayIndex) return;

      const dragIndex = draggedItem.index;
      const hoverIndex = dayIndex;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveDay(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  drag(dragHandleRef);
  drop(ref);

  return (
    <div
      ref={ref}
      className={`relative ${isDragging ? "opacity-50" : ""}`}
    >
      {children(dragHandleRef, isDragging)}
    </div>
  );
};

const getItinerarySortOrder = (day) =>
  day.sort_order ?? Math.max(0, (day.day_number ?? 1) - 1);

const formatDayAttractionsFromApi = (day, attractionMasters = []) =>
  (day.attractions || []).map((attr) => {
    const masterAttraction = attractionMasters.find(
      (master) => Number(master.id) === Number(attr.attraction_id)
    );

    return {
      id: attr.id,
      attraction_id: attr.attraction_id,
      package_itinerary_id: day.id,
      name: masterAttraction?.name || attr.name || "",
      image: null,
      image_url: masterAttraction?.image_url || attr.image_url || null,
      isNew: false,
    };
  });

const formatDaysFromApi = (apiDays, attractionMasters = []) =>
  [...apiDays]
    .sort((a, b) => getItinerarySortOrder(a) - getItinerarySortOrder(b))
    .map((day) => ({
      ...day,
      includedFood: day.foods?.map((food) => Number(food.food_id)) || [],
      attractions: formatDayAttractionsFromApi(day, attractionMasters),
    }));

const isAttractionImageLocked = (attraction) =>
  Boolean(attraction?.attraction_id) &&
  !attraction?.isNew &&
  Boolean(attraction?.image_url) &&
  !(attraction?.image instanceof File);

const isDayCompleted = (day) => Number(day?.is_completed) === 1;

const scrollToItineraryDay = (dayId) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const dayEl = document.getElementById(`itinerary-day-${dayId}`);
      dayEl?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  });
};

const AttractionSelectOption = (props) => {
  const { data, innerRef, innerProps, selectProps } = props;
  const onDeleteMaster = selectProps.onDeleteMaster;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer hover:bg-primary/5"
    >
      <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{data.label}</span>
      {data.value != null && onDeleteMaster && (
        <button
          type="button"
          title={`Remove "${data.label}" from list`}
          className="shrink-0 rounded-md p-1.5 text-red-500 transition hover:bg-red-50"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onDeleteMaster(data);
          }}
        >
          <i className="fi fi-rr-trash text-xs"></i>
        </button>
      )}
    </div>
  );
};

const ItineraryInfo = forwardRef(({
  packageid,
  setItineraryData,
  onAllDaysCompleted,
  onDaySaved,
  onPinSection,
  expectedDayCount = 0,
  onUserEdit,
}, ref) => {
  const queryClient = useQueryClient();
  const [expandedDay, setExpandedDay] = useState(null);
  const [days, setDays] = useState([]);
  const [editorContents, setEditorContents] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [savingDayId, setSavingDayId] = useState(null);
  const [deleteMasterDialog, setDeleteMasterDialog] = useState({
    open: false,
    id: null,
    label: "",
  });
  const [deleteDayDialog, setDeleteDayDialog] = useState({
    open: false,
    id: null,
    dayNumber: null,
    title: "",
  });
  const reorderPersistTimerRef = useRef(null);

  const notifyUserEdit = () => onUserEdit?.();

  // Fetch data using React Query
  const { data: itineraryData } = useQuery({
    queryKey: ["itinerary", packageid],
    queryFn: () => fetchBasicItinerary(packageid),
    enabled: !!packageid,
  });

  const { data: foodData } = useQuery({
    queryKey: ["foods"],
    queryFn: fetchItineraryFoodMaster,
  });

  const { data: attractionData } = useQuery({
    queryKey: ["attractions"],
    queryFn: fetchItineraryAttractionMaster,
  });

  const meals = foodData?.data || [];
  const attractionMasters = useMemo(() => attractionData?.data || [], [attractionData]);
  const attractionSelectOptions = useMemo(
    () =>
      attractionMasters.map((attr) => ({
        value: attr.id,
        label: attr.name,
        image_url: attr.image_url || null,
      })),
    [attractionMasters]
  );

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateItinerary(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["itinerary", packageid]);
      setShowSuccess(true);
      setSuccessMessage("Itinerary saved successfully");
    },
    onError: (error) => {
      setShowError(true);
      setError(error.message || "Failed to save itinerary");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: reorderItinerary,
    onSuccess: () => {
      queryClient.invalidateQueries(["itinerary", packageid]);
    },
    onError: (mutationError) => {
      setShowError(true);
      setError(mutationError.message || "Failed to update itinerary order");
    },
  });

  const deleteMasterMutation = useMutation({
    mutationFn: deleteItineraryAttractionMaster,
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries(["attractions"]);
      queryClient.invalidateQueries(["itinerary", packageid]);
      setDays((prev) =>
        prev.map((day) => ({
          ...day,
          attractions: (day.attractions || []).map((attr) =>
            Number(attr.attraction_id) === Number(deletedId)
              ? {
                  ...attr,
                  attraction_id: null,
                  name: "",
                  image: null,
                  image_url: null,
                  isNew: false,
                }
              : attr
          ),
        }))
      );
      setShowSuccess(true);
      setSuccessMessage("Attraction removed from list");
    },
    onError: (mutationError) => {
      setShowError(true);
      setError(
        mutationError.response?.data?.message ||
          mutationError.message ||
          "Failed to remove attraction"
      );
    },
  });

  const deleteDayMutation = useMutation({
    mutationFn: deleteItineraryDay,
    onSuccess: (_, deletedDayId) => {
      queryClient.invalidateQueries(["itinerary", packageid]);
      queryClient.invalidateQueries(["generalInfo", packageid]);
      if (expandedDay === deletedDayId) {
        setExpandedDay(null);
      }
      setValidationErrors({});
      setShowSuccess(true);
      setSuccessMessage("Itinerary day deleted successfully");
    },
    onError: (mutationError) => {
      setShowError(true);
      setError(
        mutationError.response?.data?.message ||
          mutationError.message ||
          "Failed to delete itinerary day"
      );
    },
    onSettled: () => {
      setDeleteDayDialog({ open: false, id: null, dayNumber: null, title: "" });
    },
  });

  const handleDeleteMasterOption = useCallback((option) => {
    if (!option?.value) return;
    setDeleteMasterDialog({
      open: true,
      id: option.value,
      label: option.label || "this attraction",
    });
  }, []);

  const handleConfirmDeleteMaster = () => {
    if (!deleteMasterDialog.id) return;
    deleteMasterMutation.mutate(deleteMasterDialog.id, {
      onSettled: () => {
        setDeleteMasterDialog({ open: false, id: null, label: "" });
      },
    });
  };

  const handleDeleteDayClick = (event, day) => {
    event.preventDefault();
    event.stopPropagation();
    if (days.length <= 1) {
      setShowError(true);
      setError("A package must have at least one itinerary day");
      return;
    }
    setDeleteDayDialog({
      open: true,
      id: day.id,
      dayNumber: day.day_number,
      title: day.destination || "",
    });
  };

  const handleConfirmDeleteDay = () => {
    if (!deleteDayDialog.id) return;
    notifyUserEdit();
    deleteDayMutation.mutate(deleteDayDialog.id);
  };

  const persistDayOrder = useCallback(
    (orderedDays) => {
      if (!packageid || !orderedDays?.length) return;

      if (reorderPersistTimerRef.current) {
        clearTimeout(reorderPersistTimerRef.current);
      }

      reorderPersistTimerRef.current = setTimeout(() => {
        reorderMutation.mutate({
          package_id: packageid,
          order: orderedDays.map((day, index) => ({
            id: day.id,
            sort_order: index,
            day_number: index + 1,
          })),
        });
      }, 350);
    },
    [packageid, reorderMutation]
  );

  const moveDay = useCallback(
    (dragIndex, hoverIndex) => {
      if (dragIndex === hoverIndex) return;

      notifyUserEdit();

      setDays((prevDays) => {
        const reordered = [...prevDays];
        const [movedDay] = reordered.splice(dragIndex, 1);
        reordered.splice(hoverIndex, 0, movedDay);

        const withDayNumbers = reordered.map((day, index) => ({
          ...day,
          sort_order: index,
          day_number: index + 1,
        }));

        persistDayOrder(withDayNumbers);
        return withDayNumbers;
      });
    },
    [persistDayOrder]
  );

  // Initialize days when itinerary loads from API
  useEffect(() => {
    if (!itineraryData?.data) return;
    const formattedDays = formatDaysFromApi(itineraryData.data, attractionMasters);
    setDays(formattedDays);

    const initialEditorContents = {};
    formattedDays.forEach((day) => {
      initialEditorContents[day.id] = day.description || "";
    });
    setEditorContents(initialEditorContents);
  }, [itineraryData]);

  // Enrich attraction labels when master list arrives (without wiping in-progress edits)
  useEffect(() => {
    if (!attractionMasters.length) return;
    setDays((prevDays) =>
      prevDays.map((day) => ({
        ...day,
        attractions: (day.attractions || []).map((attr) => {
          if (!attr.attraction_id) return attr;
          const master = attractionMasters.find(
            (item) => Number(item.id) === Number(attr.attraction_id)
          );
          if (!master) return attr;
          return {
            ...attr,
            name: attr.name || master.name,
            image_url: attr.image_url || master.image_url || null,
          };
        }),
      }))
    );
  }, [attractionMasters]);

  // Save data
  useEffect(() => {
    setItineraryData({
      package_id: packageid,
      days: days,
    });
  }, [days, packageid, setItineraryData, itineraryData]);

  const handleImageUpload = (dayIndex, attrIndex, file) => {
    if (!file) return;

    notifyUserEdit();

    if (!file.type.startsWith("image/")) {
      setShowError(true);
      setError("Please upload an image file");
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setDays((prev) =>
      prev.map((day, idx) => {
        if (idx !== dayIndex) return day;
        const attractions = [...(day.attractions || [])];
        const current = attractions[attrIndex];
        if (!current) return day;
        attractions[attrIndex] = {
          ...current,
          image: file,
          image_url: imageUrl,
          isNew: true,
          attraction_id: null,
        };
        return { ...day, attractions };
      })
    );
  };

  const handleAddAttraction = (dayIndex) => {
    notifyUserEdit();
    setDays((prev) =>
      prev.map((day, idx) =>
        idx === dayIndex
          ? {
              ...day,
              attractions: [
                ...(Array.isArray(day.attractions) ? day.attractions : []),
                {
                  name: "",
                  image: null,
                  image_url: null,
                  attraction_id: null,
                  isNew: false,
                },
              ],
            }
          : day
      )
    );
  };

  const handleDeleteAttraction = (dayIndex, attrIndex) => {
    notifyUserEdit();
    setDays((prev) =>
      prev.map((day, idx) => {
        if (idx !== dayIndex) return day;
        const attractions = [...(day.attractions || [])];
        const removed = attractions[attrIndex];
        if (removed?.image instanceof File && removed?.image_url) {
          URL.revokeObjectURL(removed.image_url);
        }
        attractions.splice(attrIndex, 1);
        return { ...day, attractions };
      })
    );
  };

  const handleAttractionChange = (dayIndex, attrIndex, selectedOption) => {
    notifyUserEdit();
    setDays((prev) =>
      prev.map((day, idx) => {
        if (idx !== dayIndex) return day;
        const attractions = [...(day.attractions || [])];
        const currentAttraction = attractions[attrIndex] || {};

        if (selectedOption) {
          const isExistingAttraction =
            selectedOption.value && !selectedOption.__isNew__;

          if (currentAttraction.image instanceof File && currentAttraction.image_url) {
            URL.revokeObjectURL(currentAttraction.image_url);
          }

          attractions[attrIndex] = {
            id: currentAttraction.id || null,
            attraction_id: isExistingAttraction ? selectedOption.value : null,
            package_itinerary_id: day.id,
            name: selectedOption.label,
            image: null,
            image_url: selectedOption.image_url || null,
            isNew: !isExistingAttraction,
          };
        } else {
          if (currentAttraction.image instanceof File && currentAttraction.image_url) {
            URL.revokeObjectURL(currentAttraction.image_url);
          }

          attractions[attrIndex] = {
            id: null,
            attraction_id: null,
            package_itinerary_id: day.id,
            name: "",
            image: null,
            image_url: null,
            isNew: false,
          };
        }

        return { ...day, attractions };
      })
    );
  };

  // Save day itinerary
  const handleSave = async (dayIndex, { closeAfter = false } = {}) => {
    const currentDay = days[dayIndex];
    if (!currentDay) return false;

    const destination = String(currentDay.destination || "").trim();
    const description = currentDay.description || "";

    if (!destination || !hasRichTextContent(description)) {
      setValidationErrors({
        ...(!destination ? { destination: ["Title is required"] } : {}),
        ...(!hasRichTextContent(description)
          ? { description: ["Description is required"] }
          : {}),
      });
      setShowError(true);
      setError("Please fill title and description before saving this day");
      return false;
    }

    const foods = (currentDay.includedFood || []).map((foodId) => ({
      id: foodId,
    }));

    const attractionRows = (currentDay.attractions || []).filter((attr) =>
      String(attr?.name || "").trim()
    );

    const oldAttractions = attractionRows
      .filter((attr) => attr.attraction_id && !attr.isNew)
      .map((attr) => ({
        id: attr.attraction_id,
      }));

    const formattedData = {
      destination: currentDay.destination,
      description: currentDay.description,
      day_number: currentDay.day_number,
      is_completed: 1,
      itinerary_food: JSON.stringify(foods),
      old_attractions: JSON.stringify(oldAttractions),
    };

    const newAttractions = attractionRows
      .filter((attr) => attr.isNew)
      .map((attr) => ({
        name: attr.name,
        image: attr.image,
        image_url: attr.image_url,
      }));

    newAttractions.forEach((attraction, index) => {
      formattedData[`new_attractions[${index}]`] = attraction.name;
      if (attraction.image) {
        formattedData[`new_attraction_images[${index}]`] = attraction.image;
      }
    });

    setSavingDayId(currentDay.id);

    try {
      await updateMutation.mutateAsync({
        id: currentDay.id,
        data: formattedData,
      });
      setValidationErrors({});
      onDaySaved?.();
      let nextDays = [];
      let allDaysCompleted = false;
      setDays((prev) => {
        nextDays = prev.map((day, idx) =>
          idx === dayIndex ? { ...day, is_completed: 1 } : day
        );
        const requiredDays = Math.max(
          Number(expectedDayCount) || 0,
          nextDays.length
        );
        allDaysCompleted =
          requiredDays > 0 &&
          nextDays.length >= requiredDays &&
          nextDays.every(isDayCompleted);
        return nextDays;
      });

      if (allDaysCompleted) {
        if (closeAfter) {
          setExpandedDay(null);
        }
        onAllDaysCompleted?.();
      } else if (closeAfter) {
        const nextDay = nextDays[dayIndex + 1];
        if (nextDay) {
          setExpandedDay(nextDay.id);
          onPinSection?.("itinerary", { durationMs: 8000 });
          scrollToItineraryDay(nextDay.id);
        } else {
          setExpandedDay(null);
          onPinSection?.("itinerary");
        }
      }
      return true;
    } catch (err) {
      console.error("Save failed:", err);
      if (err.response?.status === 422) {
        try {
          const errorMessage = err.response.data.message;
          const parsedErrors =
            typeof errorMessage === "string"
              ? JSON.parse(errorMessage)
              : errorMessage;
          setValidationErrors(parsedErrors);
        } catch (parseError) {
          console.error("Error parsing validation errors:", parseError);
          setValidationErrors({});
        }
      }
      return false;
    } finally {
      setSavingDayId(null);
    }
  };

  const isDaySaveable = (day) => {
    const destination = String(day?.destination || "").trim();
    return Boolean(destination && hasRichTextContent(day?.description));
  };

  const saveFromFooter = async () => {
    const indicesToSave = [];

    if (expandedDay !== null) {
      const openIndex = days.findIndex((day) => day.id === expandedDay);
      if (openIndex >= 0) indicesToSave.push(openIndex);
    }

    days.forEach((day, idx) => {
      if (indicesToSave.includes(idx)) return;
      if (isDaySaveable(day) && !isDayCompleted(day)) {
        indicesToSave.push(idx);
      }
    });

    if (!indicesToSave.length) {
      const requiredDays = Math.max(Number(expectedDayCount) || 0, days.length);
      const allDaysCompleted =
        requiredDays > 0 &&
        days.length >= requiredDays &&
        days.every(isDayCompleted);

      if (allDaysCompleted) {
        if (onAllDaysCompleted) await onAllDaysCompleted();
        return { success: true, allDaysCompleted: true };
      }

      const firstIncomplete = days.findIndex((day) => !isDayCompleted(day));
      if (firstIncomplete >= 0) {
        setExpandedDay(days[firstIncomplete].id);
        return {
          success: false,
          message: `Please complete Day ${firstIncomplete + 1} title and description`,
        };
      }

      return {
        success: false,
        message: "Please open a day and fill title and description",
      };
    }

    for (const dayIndex of indicesToSave) {
      const saved = await handleSave(dayIndex, { closeAfter: false });
      if (!saved) {
        setExpandedDay(days[dayIndex].id);
        return {
          success: false,
          message: `Please fill title and description for Day ${dayIndex + 1}`,
        };
      }
    }

    const requiredDays = Math.max(Number(expectedDayCount) || 0, days.length);
    const allDaysCompleted =
      requiredDays > 0 &&
      days.length >= requiredDays &&
      days.every(isDayCompleted);

    return { success: true, allDaysCompleted };
  };

  useImperativeHandle(ref, () => ({
    saveFromFooter,
  }));

  const handleDayClick = (dayId) => {
    if (expandedDay === dayId) {
      setValidationErrors({});
      setShowError(false);
      setError("");
      setExpandedDay(null);
      return;
    }

    setValidationErrors({});
    setShowError(false);
    setError("");
    setExpandedDay(dayId);
  };

  const handleDescriptionChange = (dayId, newContent) => {
    notifyUserEdit();
    setEditorContents((prev) => ({
      ...prev,
      [dayId]: newContent,
    }));

    setDays((prevDays) =>
      prevDays.map((day) =>
        day.id === dayId ? { ...day, description: newContent } : day
      )
    );
  };

  const handleDestinationChange = (dayIndex, value) => {
    notifyUserEdit();
    setDays((prev) =>
      prev.map((day, idx) =>
        idx === dayIndex ? { ...day, destination: value } : day
      )
    );
  };

  const handleFoodChange = (mealId, dayIndex, checked) => {
    notifyUserEdit();
    setDays((prev) =>
      prev.map((day, idx) => {
        if (idx !== dayIndex) return day;
        const includedFood = Array.isArray(day.includedFood) ? [...day.includedFood] : [];
        const numericMealId = Number(mealId);

        if (checked) {
          if (!includedFood.includes(numericMealId)) {
            includedFood.push(numericMealId);
          }
        } else {
          const nextFood = includedFood.filter((id) => id !== numericMealId);
          return { ...day, includedFood: nextFood };
        }

        return { ...day, includedFood };
      })
    );
  };

  // Add a reset function
  const resetComponent = () => {
    setExpandedDay(null);
    setDays([]);
    setEditorContents({});
  };

  // Call resetComponent when component unmounts
  useEffect(() => {
    return () => {
      if (reorderPersistTimerRef.current) {
        clearTimeout(reorderPersistTimerRef.current);
      }
      resetComponent();
    };
  }, []);

  return (
    <>
      {showError && error && (
        <Toster
          message={error}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => setShowSuccess(false)}
        />
      )}

      <Dialog
        open={deleteMasterDialog.open}
        onClose={() =>
          setDeleteMasterDialog({ open: false, id: null, label: "" })
        }
        title={
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            Remove Attraction
          </div>
        }
        primaryButtonText="Remove"
        primaryButtonAction={handleConfirmDeleteMaster}
        closeButtonText="Cancel"
        closeButtonAction={() =>
          setDeleteMasterDialog({ open: false, id: null, label: "" })
        }
        isLoading={deleteMasterMutation.isPending}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-600">
            Remove{" "}
            <span className="font-semibold text-gray-900">
              &ldquo;{deleteMasterDialog.label}&rdquo;
            </span>{" "}
            from your attraction list?
          </p>
          <p className="mt-2 text-sm text-gray-500">
            This will delete it from saved options. This action cannot be undone.
          </p>
        </div>
      </Dialog>

      <Dialog
        open={deleteDayDialog.open}
        onClose={() =>
          setDeleteDayDialog({ open: false, id: null, dayNumber: null, title: "" })
        }
        title={
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            Delete Itinerary Day
          </div>
        }
        primaryButtonText="Delete Day"
        primaryButtonAction={handleConfirmDeleteDay}
        closeButtonText="Cancel"
        closeButtonAction={() =>
          setDeleteDayDialog({ open: false, id: null, dayNumber: null, title: "" })
        }
        isLoading={deleteDayMutation.isPending}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-600">
            Delete{" "}
            <span className="font-semibold text-gray-900">
              Day {deleteDayDialog.dayNumber}
              {deleteDayDialog.title ? ` — ${deleteDayDialog.title}` : ""}
            </span>
            ?
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Later days will be renumbered and total days in General Info will
            update automatically. This cannot be undone.
          </p>
        </div>
      </Dialog>

      <div className="w-full">
        <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-5 py-3.5 shadow-sm">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              Itinerary Timeline
            </p>
            <p className="mt-0.5 text-sm font-semibold text-gray-900">
              {days.length} day{days.length !== 1 ? "s" : ""} planned
            </p>
            {days.length > 1 && (
              <p className="mt-1 text-xs text-gray-500">
                Drag the grip icon to reorder days, or delete a day from its card
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50/80 px-3 py-1 text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
              {days.filter((d) => d.is_completed === 1).length} complete
            </span>
          </div>
        </div>

        <div className="relative space-y-4">
        {days.map((day, dayIndex) => {
          const isCompletedDay = isDayCompleted(day);

          return (
            <SortableDayCard
              key={day.id}
              dayIndex={dayIndex}
              moveDay={moveDay}
            >
              {(dragHandleRef) => (
            <div
              id={`itinerary-day-${day.id}`}
              className={`relative scroll-mt-28 rounded-xl border border-gray-200/60 bg-white transition-all ${
                expandedDay === day.id ? "shadow-sm ring-1 ring-primary/10" : "shadow-sm"
              }`}
            >
              <div
                onClick={() => handleDayClick(day.id)}
                className={`flex cursor-pointer items-center justify-between px-4 py-4 transition-all duration-200 sm:px-5 ${
                  expandedDay === day.id
                    ? "border-b border-gray-100 bg-slate-50/50"
                    : "hover:bg-slate-50/80"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-semibold ${
                      isCompletedDay
                        ? "border border-emerald-100 bg-emerald-50 text-emerald-600"
                        : "bg-primary/90 text-white shadow-sm"
                    }`}
                  >
                    {isCompletedDay ? (
                      <i className="fi fi-rr-check text-[11px]"></i>
                    ) : (
                      day.day_number
                    )}
                  </div>

                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-bold text-gray-800">
                        {day.destination || "Add Title"}
                      </h2>
                    </div>
                    <span className="text-xs text-gray-500">
                      Day {day.day_number} of Itinerary
                    </span>
                  </div>
                </div>

                {/* Right Side Controls */}
                <div className="flex items-center gap-3">
                  {/* Status Indicator */}
                  <div className="hidden sm:flex items-center gap-2 rounded-full border border-gray-100 bg-slate-50 px-3 py-1">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        isCompletedDay ? "bg-emerald-400" : "bg-amber-400"
                      }`}
                    ></span>
                    <span className="text-xs font-medium text-gray-600">
                      {isCompletedDay ? "Completed" : "In Progress"}
                    </span>
                  </div>

                  <button
                    type="button"
                    className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-all duration-200 ${
                      expandedDay === day.id
                        ? "bg-primary/5 border-primary/20 text-primary"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <i
                      className={`fi ${
                        expandedDay === day.id
                          ? "fi-rr-angle-up flex item-center justify-center"
                          : "fi-rr-angle-down flex item-center justify-center"
                      } text-sm`}
                    ></i>
                  </button>

                  {days.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => handleDeleteDayClick(e, day)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-500 transition hover:border-red-200 hover:bg-red-100"
                      title="Delete day"
                    >
                      <i className="fi fi-rr-trash text-sm"></i>
                    </button>
                  )}

                  {days.length > 1 && (
                    <button
                      type="button"
                      ref={dragHandleRef}
                      onClick={(e) => e.stopPropagation()}
                      className="hidden sm:flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-400 transition hover:border-gray-300 hover:bg-gray-100 hover:text-gray-600 cursor-move"
                      title="Drag to reorder"
                    >
                      <i className="fi fi-rr-grip-dots text-sm"></i>
                    </button>
                  )}
                </div>
              </div>

              {expandedDay === day.id && (
                <div className="space-y-6 p-5 sm:p-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={day.destination || ""}
                      onChange={(e) => handleDestinationChange(dayIndex, e.target.value)}
                      className={`w-full px-3 py-1 border ${validationErrors.destination ? "border-red-500" : "border-gray-300"
                        } rounded-lg focus:outline-none focus:border-primary focus:ring-none text-sm font-medium text-gray-800`}
                      placeholder="Enter title"
                    />
                    {validationErrors.destination && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.destination[0]}</p>
                    )}
                  </div>



                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <TinyEditor
                      value={editorContents[day.id] || ""}
                      onChange={(content) => handleDescriptionChange(day.id, content)}
                      className={`${validationErrors.description ? "border-red-500" : "border-gray-300"}`}
                    />
                    {validationErrors.description && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.description[0]}</p>
                    )}
                  </div>

                  <div className=" rounded-lg p-0">
                    <div className="flex items-center justify-between  px-0 ">
                      <div className="flex items-center ">
                        <label className="block text-sm font-medium mb-2 ">
                          Choose Included Meals
                        </label>
                      </div>
                      <div className="hidden items-center gap-1 bg-primary/5 px-3 py-1 rounded-full">
                        <span className="w-2 h-2 rounded-full bg-primary"></span>
                        <span className=" text-xs font-medium text-primary">
                          {days[dayIndex]?.includedFood?.length || 0} selected
                        </span>
                      </div>
                    </div>

                    <div className=" grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                      {meals.map((meal) => {
                        const includedFood = Array.isArray(day.includedFood)
                          ? day.includedFood
                          : [];
                        const isChecked = includedFood.includes(Number(meal.id));
                        return (
                          <div
                            key={meal.id}
                            onClick={() =>
                              handleFoodChange(meal.id, dayIndex, !isChecked)
                            }
                            className={`group relative flex items-center gap-3 px-3 py-[2px] rounded-[5px] cursor-pointer transition-all duration-200
                             border border-gray-300`}
                          >
                            {/* Icon Container */}
                            <div
                              className={`shrink-0  rounded-full flex items-center justify-center
                             `}
                            >
                              <img
                                src={meal.icon_url}
                                alt={meal.name}
                                className="w-5 h-5 object-contain"
                              />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <span
                                className={`block text-sm font-medium truncate leading-none
                                `}
                              >
                                {meal.name}
                              </span>
                            </div>

                            {/* Check Indicator */}
                            <div
                              className={`absolute right-2 top-1/2 -translate-y-1/2 transition-all duration-200
                              ${isChecked ? "opacity-100" : "opacity-0"}`}
                            >
                              <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                <i className="fi fi-rr-check text-white text-[8px]"></i>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Attractions/Activities
                    </label>

                    <div className="space-y-2">
                      {(day.attractions || []).map((attraction, attrIndex) => (
                        <div
                          key={`${day.id}-attraction-${attrIndex}`}
                          className="bg-gray-50 rounded-lg p-2"
                        >
                          <div className="flex flex-col md:flex-row justify-between items-start gap-6">
                            <div className="flex-1 min-w-0 col-span-2">
                              <CreatableSelect
                                value={
                                  attraction.name
                                    ? {
                                      value:
                                        attraction.attraction_id ||
                                        attraction.id,
                                      label: attraction.name,

                                    }
                                    : null
                                }
                                onChange={(selectedOption) =>
                                  handleAttractionChange(
                                    dayIndex,
                                    attrIndex,
                                    selectedOption
                                  )
                                }
                                options={attractionSelectOptions}
                                onDeleteMaster={handleDeleteMasterOption}
                                components={{ Option: AttractionSelectOption }}
                                isClearable
                                placeholder="Select or create attraction..."
                                className="w-full"
                                classNamePrefix="select"
                                styles={creatableStyleSingle()}
                                {...selectMenuPortalProps}
                              />
                            </div>

                            <div className="flex items-center gap-2 border-l pl-4 min-w-[160px]">
                              <div className="relative w-full bt-2 flex gap-2">
                                {isAttractionImageLocked(attraction) ? (
                                  <div className="flex items-center justify-center px-4 py-1 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed w-full">
                                    <i className="fi fi-rr-picture mr-2 text-sm"></i>
                                    <span className="text-gray-600 text-sm font-medium">
                                      Image Locked
                                    </span>
                                  </div>
                                ) : (
                                  <ImageInput
                                    onChange={(file) =>
                                      handleImageUpload(
                                        dayIndex,
                                        attrIndex,
                                        file
                                      )
                                    }
                                    label=""
                                    required={false}
                                    className="w-full"
                                  />
                                )}
                                <button
                                  onClick={() =>
                                    handleDeleteAttraction(dayIndex, attrIndex)
                                  }
                                  className={pbDeleteBtn}
                                >
                                  <i className="fi fi-rr-trash flex items-center justify-center"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-2 ">
                            {(attraction.image || attraction.image_url) && (
                              <FilePreview
                                file={
                                  attraction.image instanceof File
                                    ? attraction.image
                                    : attraction.image_url
                                }
                                label="Attraction Image"
                                className="mt-2"
                                showDelete={isAttractionImageLocked(attraction)}
                                onDelete={() => {
                                  setDays((prev) =>
                                    prev.map((d, idx) => {
                                      if (idx !== dayIndex) return d;
                                      const attractions = [...(d.attractions || [])];
                                      const current = attractions[attrIndex];
                                      if (current?.image instanceof File && current?.image_url) {
                                        URL.revokeObjectURL(current.image_url);
                                      }
                                      attractions[attrIndex] = {
                                        ...current,
                                        image: null,
                                        image_url: null,
                                      };
                                      return { ...d, attractions };
                                    })
                                  );
                                }}
                                isImage={true}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleAddAttraction(dayIndex)}
                        className={pbAddBtnMt}
                      >
                        <span className="text-sm">Add More</span>
                        <i className="fi fi-rr-plus text-xs flex items-center justify-center"></i>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
              )}
            </SortableDayCard>
          );
        })}
        </div>
      </div>
    </>
  );
});

ItineraryInfo.displayName = "ItineraryInfo";

export default ItineraryInfo;
